import { Block } from "@components/Block/Block";
import { FlexibleGrid } from "@jmc/solid-design-system/src/components/atoms/FlexibleGrid/FlexibleGrid";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { default as cmsMapper } from "@utils/cms-mapper";
import { isNumber } from "lodash";
import React from "react";
import { GridSizes } from "utils/cms-mapper/CMSMapperDefinitions";

import { trackingHelpersProps } from "../types";

interface RowBlockProps {
    content: CMSBlock["row"];
    placement?: string;
    textColor?: string;
    refColor?: string;
    visitedLinkColor?: string;
    locale?: string;
    trackingHelpers: trackingHelpersProps;
    sectionBackground?: string;
    sectionIndex?: number;
    siteSpecialty?: {
        type: string;
        title: string;
    };
}

export const RowBlock = ({
    content,
    textColor,
    refColor,
    visitedLinkColor,
    placement,
    locale,
    trackingHelpers,
    sectionBackground,
    sectionIndex,
    siteSpecialty,
}: RowBlockProps): JSX.Element => {
    const rowStr = `row-${trackingHelpers.numRows}`;
    const { layout, columns } = content;
    const mappedColumns = cmsMapper.mapRowLayout(layout);
    const gridNumbers = mappedColumns.map((colSizes: GridSizes) => (isNumber(colSizes?.md) ? colSizes?.md : 1));
    const { jnjFullBranded } = useJnjBranding();
    const isMobile = useMediaQuery(BreakPoint.md);

    if (!columns || columns?.length === 0) {
        return null;
    }

    return (
        <FlexibleGrid
            fractions={gridNumbers}
            spacing={jnjFullBranded && !isMobile ? 32 : 24}
            noTopSpacing={jnjFullBranded}
        >
            {columns.map((col, colIndex) => (
                <div
                    key={`column${colIndex}`}
                    style={{
                        display: "flex",
                        alignContent: cmsMapper.mapColumnContentAlignment(col?.column?.align_content),
                        justifyContent: cmsMapper.mapColumnVerticalAlign(col?.column?.vertical_align) || "flex-top",
                        alignItems: cmsMapper.mapColumnHorizontalAlign(col?.column?.horizontal_align) || "stretch",
                        flexDirection: "column",
                    }}
                >
                    {col.column.content.map((content: CMSBlock, i: number) => (
                        <Block
                            textColor={textColor}
                            refColor={refColor}
                            visitedLinkColor={visitedLinkColor}
                            key={i}
                            content={content}
                            placement={`${placement}|${rowStr} col=${colIndex + 1}`}
                            locale={locale}
                            sectionBackground={sectionBackground}
                            sectionIndex={sectionIndex}
                            siteSpecialty={siteSpecialty}
                            columnHorizontalAlignment={col?.column?.horizontal_align}
                        />
                    ))}
                </div>
            ))}
        </FlexibleGrid>
    );
};
